import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "../../common/Modal";
import { ROUTES } from "../../../setup/constants";
import { buildTwoPartyContractRoute } from "../../../setup/util/twoPartyContract";
import { ReactComponent as CloseIcon } from "../../../assets/images/svg/twoPartyContract/close.svg";
import { ReactComponent as TwoPartyIcon } from "../../../assets/images/svg/twoPartyContract/icon-users-2.svg";
import { ReactComponent as MultiPartyIcon } from "../../../assets/images/svg/twoPartyContract/icon-users-3.svg";
import styles from "./styles.module.scss";

interface IContractTypeModalProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Asked before a new transaction is created: a two-party transaction (the organization sells,
 * Waffy holds the money) or the multi-party project where the organization is the broker.
 */
const ContractTypeModal = ({ visible, onClose }: IContractTypeModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const options = [
    {
      key: "two-party",
      icon: <TwoPartyIcon />,
      title: t("lbl_two_party_transaction"),
      description: t("lbl_two_party_transaction_desc"),
      route: buildTwoPartyContractRoute(),
    },
    {
      key: "multi-party",
      icon: <MultiPartyIcon />,
      title: t("lbl_multi_party_transaction"),
      description: t("lbl_multi_party_transaction_desc"),
      route: ROUTES.CREATE_COMPLEX_CONTRACT.replace(":complexId?/", "").replace(
        ":milestoneId?",
        ""
      ),
    },
  ];

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      disableCloseBtn
      customModalClassName={styles.modal}>
      <button
        type="button"
        aria-label={t("lbl_close")}
        className={styles.closeBtn}
        onClick={onClose}>
        <CloseIcon />
      </button>
      <div className={styles.header}>
        <h2 className={styles.title}>{t("lbl_choose_transaction_type")}</h2>
        <p className={styles.subtitle}>
          {t("lbl_choose_transaction_type_desc")}
        </p>
      </div>
      <div className={styles.options}>
        {options.map((option, index) => (
          <button
            key={option.key}
            type="button"
            // The two-party card opens focused, as the design shows it.
            autoFocus={index === 0}
            className={styles.option}
            onClick={() => {
              onClose();
              navigate(option.route);
            }}>
            <span className={styles.optionIcon}>{option.icon}</span>
            <span className={styles.optionTitle}>{option.title}</span>
            <span className={styles.optionDescription}>
              {option.description}
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
};

export default ContractTypeModal;
